import { EditRequest, StreamResponse } from '../types/api';

// Dynamic API URL detection for both development and production
// const API_BASE_URL = process.env.REACT_APP_API_URL || 
//   (typeof window !== 'undefined' && window.location.hostname === 'localhost' 
//     ? 'http://localhost:8000/api/v1'
//     : 'https://html-editor-chatbot-backend-928273906259.us-central1.run.app/api/v1');

const API_BASE_URL = typeof window !== 'undefined' && window.location.hostname === 'localhost' 
    ? 'http://localhost:8000/api/v1'
    : 'https://html-editor-chatbot-backend-928273906259.us-central1.run.app/api/v1';


export class HTMLEditorAPI {
  private static instance: HTMLEditorAPI;

  public static getInstance(): HTMLEditorAPI {
    if (!HTMLEditorAPI.instance) {
      HTMLEditorAPI.instance = new HTMLEditorAPI();
    }
    return HTMLEditorAPI.instance;
  }

  async healthCheck(): Promise<{ status: string; version: string; timestamp: number }> {
    const response = await fetch(`${API_BASE_URL}/health`);
    if (!response.ok) {
      throw new Error('Health check failed');
    }
    return response.json();
  }

  async editHTML(request: EditRequest): Promise<{
    ai_response: string;
    html_content: string;
    metadata: any;
    conversation_id: string;
    success: boolean;
  }> {
    const response = await fetch(`${API_BASE_URL}/edit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return response.json();
  }

  streamEditHTML(
    request: EditRequest,
    onMessage: (response: StreamResponse) => void,
    onError: (error: Error) => void,
    onComplete: () => void
  ): () => void {
    // Use fetch with streaming instead of EventSource since we need POST
    let abortController = new AbortController();
    
    this.streamWithFetch(request, onMessage, onError, onComplete, abortController);

    return () => {
      abortController.abort();
    };
  }

  private async streamWithFetch(
    request: EditRequest,
    onMessage: (response: StreamResponse) => void,
    onError: (error: Error) => void,
    onComplete: () => void,
    abortController?: AbortController
  ): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/edit/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(request),
        signal: abortController?.signal,
      });

      if (!response.ok) {
        throw new Error(`Stream request failed: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No readable stream available');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          onComplete();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            continue; // Skip event type lines for now
          }
          
          if (line.startsWith('data:')) {
            try {
              const jsonStr = line.slice(5).trim();
              if (jsonStr === '[DONE]') {
                onComplete();
                return;
              }
              
              const data = JSON.parse(jsonStr);
              onMessage(data);
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', line);
            }
          }
        }
      }
    } catch (error) {
      onError(error as Error);
    }
  }

  async getThreadHTML(threadId: string): Promise<{
    thread_id: string;
    html_content: string;
    timestamp: number;
    success: boolean;
  }> {
    const response = await fetch(`${API_BASE_URL}/thread/${threadId}/html`);
    if (!response.ok) {
      throw new Error(`Failed to get HTML for thread: ${response.statusText}`);
    }
    return response.json();
  }

  async updateThreadHTML(threadId: string, htmlContent: string): Promise<{
    success: boolean;
    thread_id: string;
    message: string;
    html_length: number;
    timestamp: number;
  }> {
    const response = await fetch(`${API_BASE_URL}/thread/${threadId}/html`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        html_content: htmlContent,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update HTML for thread: ${response.statusText}`);
    }

    return response.json();
  }

  async getThreadConversation(threadId: string): Promise<{
    thread_id: string;
    conversation_data: {
      thread_id: string;
      messages: Array<{
        id: string;
        type: string;
        content: string;
        timestamp: number;
        metadata?: any;
      }>;
      current_html: string;
      initial_html: string;
      conversation_id: string;
      created_at: number;
      updated_at: number;
      metadata: any;
    };
    success: boolean;
  }> {
    const response = await fetch(`${API_BASE_URL}/thread/${threadId}/conversation`);
    if (!response.ok) {
      throw new Error(`Failed to get conversation for thread: ${response.statusText}`);
    }
    return response.json();
  }

  async getConversationState(conversationId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/conversation/${conversationId}`);
    if (!response.ok) {
      throw new Error('Failed to get conversation state');
    }
    return response.json();
  }

  async publishHTML(threadId: string): Promise<{
    success: boolean;
    message: string;
    thread_id: string;
    published_url?: string;
    published_page_url?: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        thread_id: threadId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to publish HTML: ${response.statusText}`);
    }

    return response.json();
  }

  async extractThreadConversation(threadId: string): Promise<{
    thread_id: string;
    conversation_entries: Array<{
      version: string;
      step: number;
      user_message?: string;
      ai_response?: string;
      html_response?: string;
      user_intent?: string;
      current_step?: string;
    }>;
    current_html: string;
    total_entries: number;
    success: boolean;
    message?: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/thread/${threadId}/extract-conversation`);
    if (!response.ok) {
      throw new Error(`Failed to extract conversation for thread: ${response.statusText}`);
    }
    return response.json();
  }

  async listAvailableThreads(
    limit: number = 50,
    offset: number = 0,
    getTotal: boolean = false
  ): Promise<{
    threads: Array<{
      thread_id: string;
      message_count: number;
      user_messages: number;
      ai_responses: number;
      latest_version: string;
    }>;
    returned_count: number;
    limit: number;
    offset: number;
    has_more: boolean;
    total_count?: number;
    success: boolean;
  }> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      get_total: getTotal.toString()
    });
    
    const response = await fetch(`${API_BASE_URL}/threads/list?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to list threads: ${response.statusText}`);
    }
    return response.json();
  }

  async fixHTML(htmlContent: string): Promise<{
    success: boolean;
    fixed_html: string;
    original_length: number;
    fixed_length: number;
    error?: string;
    timestamp: number;
  }> {
    const response = await fetch(`${API_BASE_URL}/fix-html`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        html_content: htmlContent,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fix HTML: ${response.statusText}`);
    }

    return response.json();
  }
}

export const htmlEditorAPI = HTMLEditorAPI.getInstance(); 